import React, { useEffect, useRef, useState } from 'react'

import { storage } from '../../services/firebase'
import { serviceModals } from '../../services/modals'

import { buildComponent } from '../factory'

import * as UI from '@mantine/core'
import * as Icons from '../icons'

const rotate = UI.keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '50%': { transform: 'rotate(180deg)' },
  '100%': { transform: 'rotate(360deg)' },
})
const useStyles = UI.createStyles({
  rotate: {
    animation: `${rotate} 2s linear infinite`,
  },
})

export const GongAudioButton = buildComponent<{
  slug?: string
  entity?: string
  onUpload?: () => Promise<void>
  onDelete?: () => Promise<void>
}>()
  .withLifecycle(({ props }) => {
    const directoryPath = 'tracks/gongs/' + (props.slug ?? 'unknown')
    const trackName = 'gong'
    const trackExtension = '.m4a'
    const track = directoryPath + '/' + trackName + trackExtension

    const resetRef = useRef<() => void>(null)
    const [exists, setExists] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
      let active = true
      storage()
        .ref(track)
        .getMetadata()
        .then(() => active && setExists(true))
        .catch(() => active && setExists(false))

      return () => {
        active = false
      }
    }, [track])

    const onChange = async (file: File | null) => {
      if (!file) {
        return
      }

      setSaving(true)
      const saved = await storage()
        .ref(track)
        .put(file, {
          contentType: 'audio/mp4',
        })
        .catch(() => false)
        .then(() => true)

      if (props.onUpload && saved) {
        await props.onUpload().catch(() => {})
      }
      resetRef.current?.()
      setExists(saved)
      setSaving(false)
    }

    const onDelete = async (e: { stopPropagation: () => void }) => {
      e.stopPropagation()

      await serviceModals.confirmDeletion(
        'Supprimer la piste audio',
        'Souhaitez-vous vraiment supprimer cette piste audio ?',
      )

      setSaving(true)
      await storage()
        .ref(track)
        .delete()
        .catch(() => {})

      if (props.onDelete) {
        await props.onDelete().catch(() => {})
      }

      resetRef.current?.()
      setExists(false)
      setSaving(false)
    }

    const color = saving ? 'blue' : exists ? 'teal' : 'gray'
    const icon = saving
      ? Icons.Solid.faSpinner
      : exists
      ? Icons.Solid.faCheck
      : Icons.Solid.faUpload

    return { exists, saving, color, icon, onChange, onDelete, resetRef }
  })
  .withRender(({ lifecycle }) => {
    const styles = useStyles()

    return (
      <UI.FileButton
        resetRef={lifecycle.resetRef}
        onChange={lifecycle.onChange}
        accept={'audio/x-m4a'}
      >
        {(p) => (
          <UI.UnstyledButton {...p}>
            <UI.Badge
              radius="sm"
              variant="outline"
              color={lifecycle.color}
              styles={(theme) => ({
                root: {
                  position: 'relative',
                  height: '3rem',
                  backgroundColor: theme.fn.rgba(
                    theme.colors[lifecycle.color][0],
                    0.5,
                  ),
                  cursor: 'pointer',
                },
                inner: {
                  width: '16rem',
                  textAlign: 'center',
                },
              })}
              rightSection={
                <Icons.FontAwesomeIcon
                  icon={lifecycle.icon}
                  className={styles.cx({
                    [styles.classes.rotate]: lifecycle.saving,
                  })}
                />
              }
            >
              {lifecycle.exists && (
                <UI.Badge
                  color="gray"
                  radius="sm"
                  variant="outline"
                  onClick={lifecycle.onDelete}
                  styles={(theme) => ({
                    root: {
                      opacity: '0',
                      left: '0.5rem',
                      position: 'absolute',
                      height: '2rem',
                      width: '3rem',
                      padding: 0,
                      backgroundColor: theme.fn.rgba(theme.colors.gray[0], 0.5),
                      cursor: 'pointer',
                      '*:hover > &': {
                        opacity: 1,
                      },
                      '&:hover': {
                        color: theme.colors.red[5],
                        backgroundColor: theme.fn.rgba(
                          theme.colors.red[0],
                          0.5,
                        ),
                      },
                    },
                  })}
                >
                  <Icons.FontAwesomeIcon icon={Icons.Solid.faTrash} />
                </UI.Badge>
              )}
              Piste audio du gong
            </UI.Badge>
          </UI.UnstyledButton>
        )}
      </UI.FileButton>
    )
  })
